// Industry data — used by Home, Industries index, and IndustryDetailPage.
import {
    Store,
    Building2,
    Stethoscope,
    Factory,
    Handshake,
} from "lucide-react";

export const INDUSTRIES = [
    {
        slug: "retail",
        name: "Retail",
        icon: Store,
        oneLiner: "Multi-store retailers managing sales, stock, and people across formats.",
        hero: "Retail without the spreadsheet treadmill",
        intro:
            "From single-format chains to multi-brand portfolios, Scitus.ai gives retail operators live visibility into every store, every category, and every team member — without stitching together a dozen disconnected tools.",
        products: ["Mativex", "Karya", "Vyaahar", "Attestra"],
        challenges: [
            "Daily sales numbers arrive late or never tie out to bank receipts.",
            "Inventory ageing hides in store-level spreadsheets you never see.",
            "Roster, payroll, and statutory filings drift across formats.",
            "Vendor onboarding and PO sign-off take days when they should take minutes.",
        ],
        solutions: [
            { h: "Live sales pulse", p: "Daily revenue, AOV, returns, and category mix by 9:30 AM — across every store and channel.", product: "Mativex" },
            { h: "Inventory ageing", p: "Stockouts, ageing, and reorder recommendations driven by your actual sell-through, not gut feel.", product: "Mativex" },
            { h: "Roster & payroll", p: "Geo-aware attendance, shift swaps, and store-level wage tracking with statutory filings.", product: "Karya" },
            { h: "Vendor & PO speed", p: "Onboard a new vendor, approve a PO, and sign the contract — same day, same workflow.", product: "Attestra" },
        ],
        metrics: [
            { v: "9:30", l: "AM daily numbers" },
            { v: "40%", l: "less time on reports" },
            { v: "8 days", l: "to first dashboard" },
        ],
    },
    {
        slug: "real-estate",
        name: "Real Estate",
        icon: Building2,
        oneLiner: "Developers, brokerages, and asset managers tracking projects and capital.",
        hero: "Real estate operations that finally tie out",
        intro:
            "Developers and asset managers run two parallel businesses: a sales business and a capital project business. Scitus.ai unifies both, giving you bookings, collections, cost, and milestone visibility down to the asset.",
        products: ["Vexen", "Acrium", "Mativex", "Vyaahar", "Attestra"],
        challenges: [
            "Bookings and collections live in CRM; project costs live elsewhere.",
            "Inventory absorption and price-realisation lag the actuals by weeks.",
            "Vendor payment cycles slip; cashflow forecasts are guesswork.",
            "Buyer agreements and possession handovers move through paper.",
        ],
        solutions: [
            { h: "Bookings to handover", p: "Live demand, inventory absorption, schedule, and milestone tracking — board-pack ready every Monday.", product: "Vexen" },
            { h: "Tower-by-tower cost truth", p: "Committed vs. budget per tower, per package, per vendor — with audit-grade procurement workflows.", product: "Acrium" },
            { h: "Demand letters & milestones", p: "Linked-to-project invoicing, milestone-based receipts, and live cashflow forecasts per asset.", product: "Vyaahar" },
            { h: "Buyer agreements", p: "Agreement-to-sale, demand letters, and possession handover signed digitally — in days, not weeks.", product: "Attestra" },
            { h: "Investor reporting", p: "Lender, investor, and board reports auto-generated from a single source of project truth.", product: "Mativex" },
        ],
        metrics: [
            { v: "1 source", l: "of project truth" },
            { v: "6 weeks", l: "faster handover" },
            { v: "Live", l: "investor packs" },
        ],
    },
    {
        slug: "healthcare",
        name: "Healthcare",
        icon: Stethoscope,
        oneLiner: "Hospitals, clinics, and diagnostics groups managing revenue and people.",
        hero: "Healthcare operations without the manual reconciliation",
        intro:
            "Multi-unit healthcare groups juggle clinical, financial, and people complexity at the same time. Scitus.ai brings revenue cycle, provider productivity, and statutory compliance into one operating layer — so leadership runs the network, not the spreadsheets.",
        products: ["Mativex", "Vexen", "Karya", "Vyaahar"],
        challenges: [
            "Revenue cycle visibility lags by days; denials are tracked manually.",
            "Provider productivity sits in different systems per specialty.",
            "Roster, credentials, and statutory filings drift across units.",
            "Audit-ready financials require a fortnight of weekend work.",
        ],
        solutions: [
            { h: "Revenue cycle visibility", p: "Billing to collection, denials, and rejection tracking — daily, not monthly.", product: "Mativex" },
            { h: "Network operations", p: "OPD, OT, and bed utilisation across the network with provider-level productivity.", product: "Vexen" },
            { h: "Roster & credentials", p: "Provider rosters, credential expiries, and statutory compliance in one HR backbone.", product: "Karya" },
            { h: "Audit-ready financials", p: "P&L, balance sheet, and ageing reports with full drill-down and read-only auditor access.", product: "Vyaahar" },
        ],
        metrics: [
            { v: "28%", l: "faster revenue cycle" },
            { v: "Live", l: "denial tracking" },
            { v: "Zero", l: "manual provider rosters" },
        ],
    },
    {
        slug: "manufacturing",
        name: "Manufacturing",
        icon: Factory,
        oneLiner: "Plants and contract manufacturers tracking output and unit economics.",
        hero: "Plant intelligence beyond the ERP",
        intro:
            "Your ERP keeps the books. Scitus.ai keeps the plant honest — live machine output, yield, cost variance, and procurement intelligence layered on top of whatever ERP you run today.",
        products: ["Vexen", "Acrium", "Mativex", "Vyaahar", "Karya"],
        challenges: [
            "Daily output, yield, and scrap rates require manual aggregation.",
            "Procurement decisions happen without vendor scorecards or price benchmarks.",
            "Cost variances surface at month-end — far too late to act.",
            "Worker attendance and statutory compliance drift across shifts.",
        ],
        solutions: [
            { h: "Plant command centre", p: "Live machine output, yield, OEE, and downtime patterns at every level, across every line.", product: "Vexen" },
            { h: "Procurement intelligence", p: "Vendor scorecards, price benchmarks, on-time delivery, and audit-grade PO/GRN workflows.", product: "Acrium" },
            { h: "Capex & MRO discipline", p: "Capex projects, plant MRO purchases, and store-level inventory under one approval matrix.", product: "Acrium" },
            { h: "Daily margin", p: "Daily P&L, gross margin, and unit economics — drill from product to BOM.", product: "Mativex" },
            { h: "Worker compliance", p: "Shift attendance, wages, and statutory filings across plants and contract crews.", product: "Karya" },
        ],
        metrics: [
            { v: "12%", l: "lower procurement cost" },
            { v: "Daily", l: "unit economics" },
            { v: "OEE", l: "live by machine" },
        ],
    },
    {
        slug: "services",
        name: "Services",
        icon: Handshake,
        oneLiner: "Professional service firms and agencies that bill on time and people.",
        hero: "Services firms that run like product companies",
        intro:
            "If you bill on time, people, and outcomes, your operating system should give you live utilisation, project profitability, and pipeline health — not a fragile stack of trackers in three different tools.",
        products: ["Mativex", "Karya", "Vyaahar", "Attestra"],
        challenges: [
            "Pipeline health and forecast accuracy are gut feel, not numbers.",
            "Utilisation and billable hours arrive after the period closes.",
            "Receivables ageing surprises you at quarter-end.",
            "MSAs, SoWs, and NDAs move through email and PDF.",
        ],
        solutions: [
            { h: "Pipeline & forecast", p: "Conversion rates, forecast accuracy, and pipeline health by stage and owner.", product: "Mativex" },
            { h: "Utilisation & profitability", p: "Billable hours, project margin, and team utilisation in one view.", product: "Karya" },
            { h: "Receivables sanity", p: "Time-based invoicing, recurring billing, and ageing dashboards with auto-reminders.", product: "Vyaahar" },
            { h: "Contracts in minutes", p: "MSAs, SoWs, NDAs — templated, sent in two clicks, signed before your coffee cools.", product: "Attestra" },
        ],
        metrics: [
            { v: "8 mins", l: "to sign a SoW" },
            { v: "Live", l: "team utilisation" },
            { v: "30%", l: "fewer late invoices" },
        ],
    },
];

export const getIndustry = (slug) => INDUSTRIES.find((i) => i.slug === slug);
